import { inject, Injectable } from '@angular/core';
import { MsalService } from '@azure/msal-angular';
import { AccountInfo } from '@azure/msal-browser';
import { catchError, map, Observable, of, ReplaySubject, switchMap, tap, throwError } from 'rxjs';
import { SessionService } from './session-service';
import { HttpClient } from '@angular/common/http';
import { IApiResponse } from '../../shared/interfaces/apiResponse.interface';
import { environment } from '../../../environments/environment';
import { UserService } from '../../features/users/services/users';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private readonly msalService = inject(MsalService);
  private readonly sessionService = inject(SessionService);
  private readonly usersService = inject(UserService);
  private readonly http = inject(HttpClient);
  private readonly LOGIN_ENDPOINT = `${environment.apiUrl}/login`;

  private refreshInProgress = false;

  private refreshSubject = new ReplaySubject<boolean>(1);

  get account(): AccountInfo | null {
    return this.msalService.instance.getActiveAccount();
  }

  // MSAL

  login(): void {
    const redirectUri = `${window.location.origin}/${environment.Azure.ActiveDirectory.RedirectUri}`;
    this.msalService.loginRedirect({
      scopes: ['User.Read'],
      redirectUri,
      redirectStartPage: redirectUri,
    });
  }

  logout(): void {
    this.logoutLocal();
    const logoutUrl = `${window.location.origin}/${environment.Azure.ActiveDirectory.RedirectUriLogout}`;
    this.msalService.logoutRedirect({
      postLogoutRedirectUri: logoutUrl,
    });
  }
  logoutLocal(): void {
    this.sessionService.clear();
  }

  isADAuthenticated(): boolean {
    return !!this.account;
  }

  async initializeMsal(): Promise<void> {
    await this.msalService.instance.initialize();

    const result = await this.msalService.instance.handleRedirectPromise();

    if (result?.account) {
      this.msalService.instance.setActiveAccount(result.account);
    }

    const accounts = this.msalService.instance.getAllAccounts();

    if (!this.msalService.instance.getActiveAccount() && accounts.length > 0) {
      this.msalService.instance.setActiveAccount(accounts[0]);
    }
  }

  private loginToGetToken(email: string) {
    return this.http.post<IApiResponse<string>>(this.LOGIN_ENDPOINT, { email }).pipe(
      map(({ data }) => {
        return data;
      }),
    );
  }

  internalLogin(email: string): Observable<boolean> {
    return this.loginToGetToken(email).pipe(
      tap((token) => {
        this.sessionService.setToken(token);
      }),
      switchMap(() => this.usersService.getUserByEmail(email)),
      tap(({ data }) => {
        this.sessionService.setUser(data);
      }),
      map(() => true),
      catchError(() => {
        this.sessionService.clear();

        return of(false);
      }),
    );
  }

  completeLogin(): Observable<boolean> {
    if (this.sessionService.isAppAuthenticated()) {
      return of(true);
    }

    const account = this.account;

    if (!account) {
      return of(false);
    }

    return this.internalLogin(account.username);
  }

  refreshInternalSession(): Observable<boolean> {
    const account = this.account;

    if (!account) {
      return of(false);
    }

    return this.internalLogin(account.username);
  }

  refreshTokenIfNeeded(): Observable<boolean> {
    if (this.refreshInProgress) {
      return this.refreshSubject.asObservable();
    }

    this.refreshInProgress = true;

    this.refreshSubject = new ReplaySubject<boolean>(1);

    return this.refreshInternalSession().pipe(
      tap((success) => {
        this.refreshSubject.next(success);
        this.refreshSubject.complete();

        this.refreshInProgress = false;
      }),
      catchError((error) => {
        this.refreshSubject.next(false);
        this.refreshSubject.complete();

        this.refreshInProgress = false;

        return throwError(() => error);
      }),
    );
  }

  restoreSession(): Observable<boolean> {
    const account = this.account;

    if (!account) {
      this.sessionService.clear();

      return of(true);
    }

    const tokenExists = this.sessionService.isAppAuthenticated();

    if (!tokenExists) {
      return this.refreshInternalSession().pipe(
        tap((success) => {
          if (!success) {
            this.sessionService.clear();
          }
        }),
      );
    }

    const cachedUser = this.sessionService.loadUser();

    if (cachedUser) {
      return of(true);
    }

    return this.usersService.getUserByEmail(account.username).pipe(
      tap(({ data }) => {
        this.sessionService.setUser(data);
      }),
      map(() => true),
      catchError(() => {
        return this.refreshInternalSession().pipe(
          tap((success) => {
            if (!success) {
              this.sessionService.clear();
            }
          }),
        );
      }),
    );
  }
}
